import React, { useState } from 'react';
import { Info } from 'lucide-react';

import { Button } from './ui/button';
import { allowanceNeedsAttention } from '../lib/proRata';
// ⚠️ THE ONE PLACE THAT KNOWS WHETHER SOMEBODY READS DAYS OR HOURS — `lib/leaveUnits.js`.
// Rob switched this employee to hours and the note carried on saying "days" at them.
import { formatLeave } from '../lib/leaveUnits';

/**
 * "This person works fewer days than the company — is their allowance right?"
 *
 * ⚠️ ROB FOUND THE PROBLEM ON HIS OWN DATA, 13/08/2026: a Monday-only employee holding
 * the full company default of 20 days, for a job of about 52 days a year, with nothing
 * anywhere saying a word. His steer on where to put it: *"it needs to say something when
 * they are adding the employee or changing their hours / days they work... otherwise you
 * could be setting up a new starter and completely miss this."*
 *
 * ⚠️ THREE OPTIONS, NOT TWO, AND THAT WAS HIS CORRECTION. I offered "use 4" or "keep 20";
 * he pointed out the obvious — *"they might think well, he has got 5 bank holidays, he
 * can't have any more"* — so there is a box to type their own figure. **Our arithmetic is
 * a starting point, not a verdict.**
 *
 * ⚠️ NOT A WORD ABOUT THE LAW. Statutory floors live in ONE place and we model only a few
 * countries; Rob on extending that: *"the rest of the world we don't know and we don't
 * want to know as that has headache written all over it."* Everything here is proportion,
 * which is true everywhere. The **total time off** line is what stops "4 days" reading as
 * alarming, and it needs no legal claim to do it.
 *
 * ⚠️ AND NEVER "HE" OR "SHE" — Rob, same day. A name gives away nothing, and it is not the
 * app's business anyway. `CONVENTIONS.md` carries the rule; `pronouns.checks.mjs` enforces
 * it on everything a customer can read.
 */
// "1 day", never "1 days" — the allowance box can hold 1 (Rob's test, 22/10/2026).
const daysWord = (n) => (Number(n) === 1 ? 'day' : 'days');

const AllowanceProRataNote = ({
  name,
  allowanceDays,
  companyDefaultDays,
  // ⚠️ DAYS A WEEK, NOT HOURS (22/10/2026): their days against the company's — see
  // lib/proRata.js. `dayMinutes` is still the company's day, for the hours wording only.
  theirDaysAWeek,
  companyDaysAWeek,
  dayMinutes,
  theirDayMinutes = null,
  displayUnit = 'days',
  bankHolidaysLanding = null,
  onChoose,
}) => {
  const verdict = allowanceNeedsAttention({
    allowanceDays, companyDefaultDays, theirDaysAWeek, companyDaysAWeek,
  });

  // ⚠️ DISMISS HAS TO ACTUALLY DISMISS. The first version's third button called
  // `onChoose(null)`, which correctly wrote nothing — and so did nothing at all. Rob:
  // *"it seems like the leave it at 20 days [doesn't work] either."* Right: a button that
  // is honest about writing nothing still has to DO something, or it reads as broken.
  const [dismissed, setDismissed] = useState(null);
  // ⚠️ AND IT COMES BACK IF THE SITUATION CHANGES. Keyed on the verdict, so dismissing it
  // for "1 day a week on 20 days" does not also silence it after somebody moves them to
  // three days — which is a different question deserving a fresh answer.
  const key = verdict ? `${verdict.direction}:${verdict.suggested}:${allowanceDays}` : null;
  if (!verdict || dismissed === key) return null;

  // ⚠️ WHAT THIS PERSON ACTUALLY READS. Rob, 13/08/2026: *"I changed Butt to hourly, i.e.
  // show their leave in hours — our new pop up box kind of needs a what if they are hourly
  // side to it."* Quite right: it was hard-coded to days and said "9 days off in total" to
  // somebody whose every other screen talks in hours.
  //
  // ⚠️ THE ACTION STAYS IN DAYS, AND THAT IS DELIBERATE. The allowance box above is
  // labelled "Annual allowance (days)" and stores days — so "Use 4 days" describes exactly
  // what the button does to that box. What changes is the CONSEQUENCE, shown in the unit
  // they read. Converting the button too would put a figure in the box that did not match
  // its own label, which is a worse lie than the one being fixed.
  //
  // ⚠️ THEIR DAY, NOT THE COMPANY'S. A day of leave is worth this person's own day length,
  // which is why `day_minutes_basis` exists on the allowance row. Falling back to the
  // company's day is right when we cannot work theirs out, and it is what the app's own
  // resolution chain does.
  const perDay = theirDayMinutes || dayMinutes;
  const inTheirUnit = (days) => (
    displayUnit === 'hours' && perDay
      ? ` (${formatLeave(days, { unit: 'hours', minutes: days * perDay })})`
      : '');

  // ⚠️ THE ACTUAL COUNT. This was hours ÷ the company's day, which for John (five short
  // days) came out blank and printed "John works days a week" (Rob, 22/10/2026).
  const theirDays = theirDaysAWeek;
  const companyDays = companyDaysAWeek;
  const { direction, suggested } = verdict;
  const who = name || 'This person';

  return (
    <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
      data-testid="pro-rata-note">
      <div className="flex gap-2">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm space-y-2">
          {direction === 'over' ? (
            <>
              <p>
                <strong>{who} works {theirDays} day{theirDays === 1 ? '' : 's'} a week.</strong>
                {companyDays ? ` The company works ${companyDays}.` : ''}
              </p>
              <p className="text-muted-foreground">
                They currently have the company default of {companyDefaultDays} days
                {inTheirUnit(companyDefaultDays)}. In proportion to the days they work,
                that would be {suggested} days{inTheirUnit(suggested)}.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>{who} now works a full week</strong> but still has{' '}
                {allowanceDays} {daysWord(allowanceDays)}{inTheirUnit(allowanceDays)}, less than the company
                default of {companyDefaultDays} days{inTheirUnit(companyDefaultDays)}.
              </p>
              <p className="text-muted-foreground">
                That looks like a part-time allowance that was never updated.
              </p>
            </>
          )}

          {/* ⚠️ THE LINE THAT STOPS A SMALL NUMBER LOOKING BROKEN. "4 days" on its own
              reads as a mistake; "4 days, plus the 5 bank holidays that fall on their
              working days" reads as an arrangement. Shown only when we actually know the
              landing count — a guess here would be worse than silence. */}
          {direction === 'over' && bankHolidaysLanding !== null && bankHolidaysLanding > 0 && (
            <p className="text-muted-foreground">
              Either way they also get the {bankHolidaysLanding} bank holiday
              {bankHolidaysLanding === 1 ? '' : 's'} that fall on their working days — so{' '}
              {suggested} days would give them {suggested + bankHolidaysLanding} days off
              in total{inTheirUnit(suggested + bankHolidaysLanding)}.
            </p>
          )}

          {/* ⚠️ NO NESTED FORM AND NO "SET" BUTTON — BOTH WERE BUGS, 13/08/2026.
              The third option used to be its own little `<form>` with a submit button,
              inside the employee dialog, which is ITSELF a form. **Nested forms are
              invalid HTML**: the browser drops the inner one, so "Set" submitted the
              DIALOG — saving the employee and closing the window. Rob, one minute into
              testing: *"the set button closes the window."*

              His fix was better than a repair: *"I think the set button is another
              unnecessary step, the save button should save and set the entered days."*
              The allowance box is directly above this note. Typing in it IS setting your
              own figure, and the dialog's own Save button already saves it. So the third
              option is a sentence, not a control. */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="button" size="sm" variant="outline"
              onClick={() => onChoose(suggested)}
              data-testid="pro-rata-use-suggested">
              Use {suggested} days
            </Button>
            <span className="text-sm text-muted-foreground" data-testid="pro-rata-own-hint">
              or type your own figure in the box above
            </span>
            <Button type="button" size="sm" variant="ghost"
              onClick={() => setDismissed(key)}
              data-testid="pro-rata-dismiss">
              Leave it at {allowanceDays} {daysWord(allowanceDays)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllowanceProRataNote;
